import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../../common/components/loading/loading";
import { getUomHistory } from "../../../common/services/markets/uomService";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const UomHistory = ({ uomId }) => {
  const { t } = useTranslation(["markets"]);
  const [uomHistory, setUomHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paginationState, setPaginationState] = useState({
    currentPage: 0,
    pageSize: 10,
  });

  // Fetch UOM history on component mount
  useEffect(() => {
    const fetchHistory = async () => {
      const data = await getUomHistory(uomId);
      const formattedData = mapToViewModel(data);
      setUomHistory(_.orderBy(formattedData, "id", "desc"));
      setIsLoading(false);
    };
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uomId]);

  // Map API data to view model with formatted dates
  const mapToViewModel = useCallback((uoms) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return uoms.map((uom) => ({
      id: uom.id,
      name: uom.name,
      uomTypeName: uom.uomTypeName,
      active: uom.active,
      updatedByUserAccountId: uom.createdByUserAccountId,
      updatedBy:
        uom.updatedByUserAccountFirstName +
        " " +
        uom.updatedByUserAccountLastName,
      updatedDate: new Date(uom.updatedDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      writeDate: new Date(uom.writeDate).toLocaleDateString(
        "en-US",
        dateFormatOptions
      ),
      eventId: uom.eventId,
    }));
  }, []);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "id",
        headerName: t("RecordID"),
        sortable: true,
        filter: true,
        sort: "desc",
      },
      {
        field: "name",
        headerName: t("UomCode"),
        sortable: true,
        filter: true,
      },
      {
        field: "uomTypeName",
        headerName: t("UomType"),
        sortable: true,
        filter: true,
      },
      {
        field: "active",
        headerName: t("Active"),
        flex: 1,
        // Apply centered class for better alignment
        cellClass: "ag-center-aligned-cell",
        headerClass: "ag-center-aligned-header",
        filterValueGetter: (params) => params.data.activeString,
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
      {
        field: "updatedBy",
        headerName: t("UpdatedBy"),
        sortable: true,
        filter: true,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "writeDate",
        headerName: t("WriteDate"),
        sortable: true,
        filter: true,
      },
      {
        field: "eventId",
        headerName: t("EventID"),
        sortable: true,
        filter: true,
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      flex: 1,
    }),
    []
  );

  // Pagination configuration
  const paginationConfig = useMemo(
    () => ({
      enabled: true,
      pageSize: paginationState?.pageSize || 10,
      pageSizeOptions: [10, 20, 50, 100],
    }),
    [paginationState?.pageSize]
  );

  // Handle pagination changes
  const handlePaginationChanged = useCallback((params) => {
    if (params && params.api) {
      setPaginationState({
        currentPage: params.api.paginationGetCurrentPage(),
        pageSize: params.api.paginationGetPageSize(),
      });
    }
  }, []);

  // Grid ready handler
  const onGridReady = useCallback(
    (params) => {
      if (paginationState && paginationState.currentPage >= 0) {
        setTimeout(() => {
          params.api.paginationGoToPage(paginationState.currentPage);
        }, 0);
      }
    },
    [paginationState]
  );

  if (isLoading) return <Loading />;

  return (
    <div className="history-table-container">
      <div className="mb-3">
        {t("UOMID")} {uomId}
      </div>
      <div style={{ height: "600px", width: "100%" }}>
        <ThemeableAgGrid
          className="table-extra-small"
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowData={uomHistory}
          getRowId={(params) => params.data.id.toString()}
          animateRows={true}
          onGridReady={onGridReady}
          rowHeight={40}
          domLayout="autoHeight"
          pagination={true}
          paginationPageSize={paginationConfig.pageSize}
          paginationPageSizeSelector={paginationConfig.pageSizeOptions}
          suppressPaginationPanel={false}
          onPaginationChanged={handlePaginationChanged}
        />
      </div>
    </div>
  );
};

export default UomHistory;

